/**
 * =============================================================================
 * COLLECTION GRID
 * =============================================================================
 *
 * Single file that manages all card behaviour on collection pages.
 * Works for both:
 *   - Category pages  (Liquid-rendered cards via card-product.liquid)
 *   - Shop All page   (JS-rendered cards via custom-product-listing.js)
 *
 * RESPONSIBILITIES
 * ----------------
 *   1. Images      — swap each card slide to the finish image from collections-all.json
 *   2. Swiper      — one instance per card, bidirectionally synced with swatch radios
 *   3. Swatches    — hide/show invalid swatches based on Cloudflare product data
 *   4. Wishlist    — patch wishlist button data-attrs on swatch change
 *   5. Share       — keep share URL in sync with the active swatch SKU
 *
 * PUBLIC API (call from facets.js, custom-product-listing.js, etc.)
 * -----------------
 *   window.CollectionGrid.patchAllCards()   — re-patch every card on the page
 *   window.CollectionGrid.patchCard(card)   — patch a single card element
 *   window.CollectionGrid.resolveImages()   — re-run image resolution pass
 *
 * =============================================================================
 */

(function () {
  'use strict';


  /* =========================================================================
   * 1. IMAGES
   * -------------------------------------------------------------------------
   * Images come exclusively from category_swatches[].image_url in
   * collections-all.json (the product_media metafield CloudFront URL).
   * The Shopify featured image is never used — if CF data isn't ready yet
   * the function exits early and waits for the data to arrive.
   * ======================================================================= */

  // Normalize a CloudFront image filename to uppercase.
  // S3 is case-sensitive; SKU files are always uppercase (e.g. DV11-SC.jpg)
  // but the KV / metafield may store lowercase (dv11-sc.jpg).
  function normalizeCloudfrontUrl(url) {
    if (!url || url.indexOf('cloudfront.net') === -1) return url;
    var lastSlash = url.lastIndexOf('/');
    var base      = url.substring(0, lastSlash + 1);
    var filename  = url.substring(lastSlash + 1);
    var dotIdx    = filename.lastIndexOf('.');
    if (dotIdx === -1) return url;
    return base + filename.substring(0, dotIdx).toUpperCase() + filename.substring(dotIdx).toLowerCase();
  }

  function resolveSlideImage(img) {
    var sku = img.dataset.sku;
    var card = img.closest('[data-product-handle]');
    if (!sku || !card) return;

    // Wait until CF data is a populated array
    if (!Array.isArray(window.allCloudflareProducts) || !window.allCloudflareProducts.length) return;

    var skeleton = img.previousElementSibling;
    var handle = card.getAttribute('data-product-handle');
    var cfProduct = window.allCloudflareProducts.find(function (p) { return p.handle === handle; });

    var imageUrl = '';
    if (cfProduct) {
      var swatch = (cfProduct.category_swatches || []).find(function (s) {
        return s.sku === sku;
      });
      if (swatch && swatch.image_url) imageUrl = swatch.image_url;
    }

    // No swatch image — fall back to main product image then Shopify featured image
    if (!imageUrl && cfProduct && cfProduct.product_media) imageUrl = cfProduct.product_media;
    if (!imageUrl) imageUrl = card.getAttribute('data-featured-image') || '';

    var placeholder = card.getAttribute('data-placeholder-image') || '';

    function showImg() {
      img.style.opacity = '1';
      if (skeleton && skeleton.classList.contains('card-img-skeleton')) {
        skeleton.classList.add('hidden');
      }
    }

    if (!imageUrl) {
      if (placeholder) img.src = placeholder;
      showImg();
      return;
    }

    // Normalize CloudFront filename to uppercase before attempting load.
    var normalizedUrl = normalizeCloudfrontUrl(imageUrl);

    // 3-step fallback: normalized → original (if different) → placeholder
    function tryOriginal() {
      if (imageUrl !== normalizedUrl) {
        // Original URL differs (was lowercase) — try it as a second attempt
        img.onerror = function () {
          img.onerror = null;
          img.onload  = null;
          if (placeholder) img.src = placeholder;
          showImg();
        };
        img.onload = function () { img.onload = null; showImg(); };
        img.src = imageUrl;
        if (img.complete && img.naturalWidth) showImg();
      } else {
        // No difference — go straight to placeholder
        if (placeholder) img.src = placeholder;
        showImg();
      }
    }

    // Show image only after it finishes loading (skeleton stays until then)
    img.onerror = function () {
      img.onerror = null;
      img.onload  = null;
      tryOriginal();
    };
    img.onload = function () {
      img.onload = null;
      showImg();
    };
    img.src = normalizedUrl;
    // Handle already-cached images where onload may not fire
    if (img.complete && img.naturalWidth) showImg();
  }

  function resolveAllSlideImages() {
    document
      .querySelectorAll('.card_product_block_img_slider img[data-sku]')
      .forEach(resolveSlideImage);
  }


  /* =========================================================================
   * 2. SWIPER
   * -------------------------------------------------------------------------
   * One Swiper per card. Bidirectional sync:
   *   swatch click  → slide to matching index
   *   slide change  → check matching swatch radio
   * ======================================================================= */

  function initCardSwiper(card) {
    var sliderEl = card.querySelector('.card_product_block_img_slider');
    if (!sliderEl || sliderEl.swiper) return;
    if (typeof Swiper === 'undefined') return;

    var swiper = new Swiper(sliderEl, {
      slidesPerView: 1,
      spaceBetween: 0,
      navigation: {
        nextEl: card.querySelector('.card_product_block_img_slider_next'),
        prevEl: card.querySelector('.card_product_block_img_slider_prev'),
      },
      on: {
        slideChange: function () {
          var activeSlide = this.slides[this.activeIndex];
          if (!activeSlide) return;
          var sku = activeSlide.getAttribute('data-media-id');
          var radio = card.querySelector('.swatch-input__input[data-sku="' + sku + '"]');
          if (radio) {
            radio.checked = true;
            radio.dispatchEvent(new Event('change', { bubbles: true }));
            patchCardWishlist(card);
          }
        },
      },
    });

    _updateNavVisibility(card);

    card.querySelectorAll('.swatch-input__input').forEach(function (radio) {
      radio.onclick = function (e) {
        var sku = e.target.getAttribute('data-sku');
        if (!sku || !swiper) return;
        for (var i = 0; i < swiper.slides.length; i++) {
          if (swiper.slides[i].getAttribute('data-media-id') === sku) {
            swiper.slideTo(i);
            break;
          }
        }
      };
    });
  }

  function _updateNavVisibility(card) {
    var slides = card.querySelectorAll('.swiper-slide');
    var next = card.querySelector('.card_product_block_img_slider_next');
    var prev = card.querySelector('.card_product_block_img_slider_prev');
    if (!next || !prev) return;
    var hide = slides.length <= 1;
    next.style.display = hide ? 'none' : '';
    prev.style.display = hide ? 'none' : '';
  }


  /* =========================================================================
   * 3. SWATCH VISIBILITY
   * -------------------------------------------------------------------------
   * After Cloudflare data loads, remove swatches and slides for SKUs that
   * have no matching entry in category_swatches. Destroys+re-creates Swiper
   * when the slide count changes.
   * ======================================================================= */

  function patchSwatches(card) {
    if (!Array.isArray(window.allCloudflareProducts)) return;

    var handle = card.getAttribute('data-product-handle');
    var cfProduct = window.allCloudflareProducts.find(function (p) {
      return p.handle === handle;
    });

    var sliderEl = card.querySelector('.card_product_block_img_slider');
    var swiperDirty = false;

    // No swatches in CF Worker JSON — hide swatch bar, wishlist, share, swiper arrows, and strip ?sku= from card links
    if (!cfProduct || !Array.isArray(cfProduct.category_swatches) || cfProduct.category_swatches.length === 0) {
      var swatchBar = card.querySelector('.card_product_block_swatch');
      if (swatchBar) swatchBar.style.display = 'none';
      var wlLi = card.querySelector('.cl_wishlist');
      if (wlLi) wlLi.style.display = 'none';
      var shareLi = card.querySelector('.cl_share');
      if (shareLi) shareLi.style.display = 'none';

      // Hide swiper arrows
      var nextBtn = card.querySelector('.card_product_block_img_slider_next');
      var prevBtn = card.querySelector('.card_product_block_img_slider_prev');
      if (nextBtn) nextBtn.style.display = 'none';
      if (prevBtn) prevBtn.style.display = 'none';

      // Strip ?sku= parameter from all card links
      var prodUrl = card.getAttribute('data-product-url') || '';
      card.querySelectorAll('a[href]').forEach(function (link) {
        if (prodUrl) {
          link.setAttribute('href', prodUrl);
        } else {
          try {
            var url = new URL(link.href, window.location.origin);
            url.searchParams.delete('sku');
            link.setAttribute('href', url.toString());
          } catch (e) {}
        }
      });
      return;
    }

    var productTitle = cfProduct.title || '';

    card.querySelectorAll('.swatch-input__input').forEach(function (radio) {
      var sku = radio.getAttribute('data-sku');
      if (!sku) return;

      var cfSwatch = cfProduct.category_swatches.find(function (s) {
        return s.sku && s.sku.toLowerCase() === sku.toLowerCase();
      });
      var swatchGrp = radio.closest('.swatch_grp');
      var slide = card.querySelector('[data-media-id="' + sku + '"]');

      if (cfSwatch) {
        // Populate all data-* attributes from CF JSON
        radio.setAttribute('data-variant-id',            cfSwatch.variant_id || '');
        radio.setAttribute('data-variant-price',         cfSwatch.variant_price || '');
        radio.setAttribute('data-variant-compare-at-price', cfSwatch.variant_price || '');
        radio.setAttribute('data-variant-url',           cfSwatch.product_handle ? '/products/' + cfSwatch.product_handle : '');
        radio.setAttribute('data-variant-image',         cfSwatch.image_url || '');
        radio.setAttribute('data-variant-title',         productTitle + (cfSwatch.name ? ' - ' + cfSwatch.name : ''));
        radio.setAttribute('data-product-id',            cfSwatch.product_id || '');
        radio.setAttribute('title',                      cfSwatch.name || '');

        var label = card.querySelector('label[for="' + radio.id + '"]');
        if (label) {
          label.setAttribute('data-product-id', cfSwatch.product_id || '');
          label.setAttribute('title', cfSwatch.name || '');
        }

        // Set swatch dot colour
        var span = swatchGrp ? swatchGrp.querySelector('.swatch') : null;
        if (span && cfSwatch.color_code) span.style.backgroundColor = cfSwatch.color_code;

        // Show the swatch group
        if (swatchGrp) swatchGrp.style.display = '';

        if (slide && slide.classList.contains('swiper-slide-hidden')) {
          slide.classList.remove('swiper-slide-hidden');
          slide.classList.add('swiper-slide');
          slide.style.display = '';
          swiperDirty = true;
        }
      } else {
        // SKU has no entry in CF — remove dot and slide
        if (swatchGrp) swatchGrp.parentNode.removeChild(swatchGrp);
        if (slide) { slide.parentNode.removeChild(slide); swiperDirty = true; }
      }
    });

    // Fallback slide: hide when real slides exist, show when none do
    var fallbackSlide = card.querySelector('[data-media-id="fallback"]');
    var visibleSlides = card.querySelectorAll('.swiper-slide');
    if (visibleSlides.length > 0 && fallbackSlide) {
      fallbackSlide.classList.remove('swiper-slide');
      fallbackSlide.classList.add('swiper-slide-fallback');
      fallbackSlide.style.display = 'none';
    } else if (visibleSlides.length === 0 && fallbackSlide) {
      fallbackSlide.classList.remove('swiper-slide-fallback');
      fallbackSlide.classList.add('swiper-slide');
      fallbackSlide.style.display = '';
      swiperDirty = true;
    }

    if (swiperDirty && sliderEl && sliderEl.swiper) {
      sliderEl.swiper.destroy(true, true);
      sliderEl.swiper = null;
    }

    _updateNavVisibility(card);
  }


  /* =========================================================================
   * 4. WISHLIST + SHARE SYNC
   * -------------------------------------------------------------------------
   * On swatch change: look up the real SKU product_id + variant_id from
   * Cloudflare data and write the correct data-* attrs to:
   *   - Wishlist (specbook) button
   *   - WishlistHero wrapper element
   *   - Share anchor (keeps URL in sync with active SKU)
   * ======================================================================= */

  function patchCardWishlist(card) {
    var activeInput = card.querySelector('.swatch-input__input:checked');
    if (!activeInput) return;

    var sku        = activeInput.dataset.sku;
    var variantId  = activeInput.dataset.variantId;
    var imageUrl   = activeInput.dataset.variantImage;
    var title      = activeInput.dataset.variantTitle;
    var variantUrl = activeInput.dataset.variantUrl || '';

    var wlhBtn = card.querySelector('.js_specbook_button')
              || card.querySelector('.cl_wishlist [data-epi]');
    if (!wlhBtn) return;

    var resolvedProductId = null;
    var resolvedVariantId = variantId;

    if (Array.isArray(window.allCloudflareProducts) && sku) {
      var handle = card.getAttribute('data-product-handle');
      var cfProduct = window.allCloudflareProducts.find(function (p) {
        return p.handle === handle;
      });
      if (cfProduct && cfProduct.category_swatches) {
        var match = cfProduct.category_swatches.find(function (s) {
          return s.sku && s.sku.toLowerCase() === sku.toLowerCase();
        });
        if (match) {
          if (match.product_id) resolvedProductId = match.product_id;
          if (match.variant_id) resolvedVariantId = match.variant_id;
        }
      }
    }

    wlhBtn.setAttribute('data-wlh-fc', sku);
    wlhBtn.setAttribute('data-wlh-fn', title);
    if (imageUrl) wlhBtn.setAttribute('data-wlh-image', imageUrl);

    var wrapper = wlhBtn.closest('.wishlist-hero-custom-button');

    // Only update variant/product IDs when we have a valid variant — otherwise
    // keep the Liquid-rendered defaults so WishlistHero always gets a valid pair.
    if (resolvedVariantId) {
      wlhBtn.setAttribute('data-epi', resolvedVariantId);
      wlhBtn.setAttribute('data-epi-orignal', resolvedVariantId);
      if (resolvedProductId) {
        wlhBtn.setAttribute('data-empi', resolvedProductId);
        wlhBtn.setAttribute('data-empi-orignal', resolvedProductId);
      }
      if (wrapper) {
        wrapper.setAttribute('data-wlh-variantid', resolvedVariantId);
        if (resolvedProductId) wrapper.setAttribute('data-wlh-id', resolvedProductId);
      }
    }

    if (variantUrl) {
      var fullVariantUrl = window.location.origin + variantUrl;
      wlhBtn.setAttribute('data-du', fullVariantUrl);
      wlhBtn.setAttribute('data-du-orignal', fullVariantUrl);
      if (wrapper) wrapper.setAttribute('data-wlh-link', fullVariantUrl);
    }
    if (wrapper && imageUrl) wrapper.setAttribute('data-wlh-image', imageUrl);

    var shareBtn = card.querySelector('.btn-share');
    if (shareBtn && sku) {
      var base = (shareBtn.getAttribute('data-product-url') || '').split('?')[0];
      shareBtn.setAttribute('data-product-url', base + '?sku=' + sku);
    }

    if (window.Wishlist && typeof window.Wishlist.refreshButtonState === 'function') {
      var empi = wlhBtn.getAttribute('data-empi');
      var du   = wlhBtn.getAttribute('data-du');
      window.Wishlist.refreshButtonState(resolvedVariantId, empi, du);
    }
  }


  /* =========================================================================
   * 5. ORCHESTRATOR
   * ======================================================================= */

  function patchCard(card) {
    patchSwatches(card);
    patchCardWishlist(card);
    initCardSwiper(card);
  }

  function patchAllCards() {
    document.querySelectorAll('.card_product_block').forEach(patchCard);
    resolveAllSlideImages();
  }

  // Init Swipers + wishlist immediately (no CF data required)
  function _initCards() {
    document.querySelectorAll('.card_product_block').forEach(function (card) {
      patchCardWishlist(card);
      initCardSwiper(card);
    });
  }


  /* =========================================================================
   * 6. CF DATA LOADER
   * -------------------------------------------------------------------------
   * Self-contained fetch for collections-all.json so images load on any
   * page without depending on another script to dispatch the event first.
   *
   * Uses window.__cloudflareProductsPromise as a shared lock so only one
   * network request is made even when multiple scripts call this.
   * The promise always RESOLVES (never rejects) — failures resolve to null.
   * ======================================================================= */

  // Fallback: show featured image / placeholder for any card that still has
  // blank images after CF data failed or timed out.  Also un-hides swatches
  // so the dot row is at least visible even without colours.
  function _showFallbackImages() {
    document.querySelectorAll('.card_product_block').forEach(function (card) {
      var featuredImage = card.getAttribute('data-featured-image') || '';
      var placeholder   = card.getAttribute('data-placeholder-image') || '';
      var fallbackSrc   = featuredImage || placeholder;

      card.querySelectorAll('.card_product_block_img_slider img[data-sku]').forEach(function (img) {
        if (img.style.opacity === '0') {
          if (fallbackSrc) img.src = fallbackSrc;
          img.style.opacity = '1';
          var skeleton = img.previousElementSibling;
          if (skeleton && skeleton.classList.contains('card-img-skeleton')) {
            skeleton.classList.add('hidden');
          }
        }
      });

      // Reveal swatch dots (colours stay grey — better than invisible)
      card.querySelectorAll('.swatch_grp').forEach(function (grp) {
        if (grp.style.display === 'none') grp.style.display = '';
      });
    });
  }

  function _ensureCfData() {
    // Already available — resolve images immediately
    if (Array.isArray(window.allCloudflareProducts) && window.allCloudflareProducts.length) {
      patchAllCards();
      return;
    }

    var isShopAll = (typeof current_collection !== 'undefined' && current_collection === 'all') || (window.location && window.location.pathname && window.location.pathname.indexOf('/collections/all') !== -1);
    var base = isShopAll ? window.worker_base_url : (window.category_worker_base_url || window.worker_base_url);
    if (!base) {
      console.warn('[CollectionGrid] window.worker_base_url not set — showing fallback images');
      _showFallbackImages();
      return;
    }

    var country = (typeof country_code !== 'undefined' && country_code ? country_code : (window.shopify_country_code || (window.localization && window.localization.country && window.localization.country.iso_code) || 'AU')).toUpperCase();
    var cfUrl   = base + '/api/custom/collections-all.json?country=' + encodeURIComponent(country);

    // Show fallback images if CF data hasn't arrived within 5 seconds
    var fallbackTimer = setTimeout(function () {
      if (Array.isArray(window.allCloudflareProducts) && window.allCloudflareProducts.length) return;
      console.warn('[CollectionGrid] CF data timeout — showing fallback images');
      _showFallbackImages();
    }, 5000);

    // Reuse an in-flight promise if another script (custom-product-listing.js,
    // main-collection-product-grid.liquid) already started the fetch.
    // If the existing promise rejects (another script re-throws), handle it
    // gracefully so we don't get an unhandled rejection.
    var existingPromise = window.__cloudflareProductsPromise || window._cloudflareProductsPromise || window.__cloudflareProductsSearchPromise;
    if (!existingPromise) {
      existingPromise = fetch(cfUrl)
        .then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.json();
        })
        .then(function (data) {
          if (!Array.isArray(data)) throw new Error('Worker returned non-array: ' + JSON.stringify(data).slice(0, 100));
          window.allCloudflareRawProducts = data;
          window.allCloudflareProducts    = data;
          return data;
        })
        .catch(function (err) {
          console.warn('[CollectionGrid] collections-all.json unavailable:', err.message);
          window.__cloudflareProductsPromise = null;
          window._cloudflareProductsPromise = null;
          window.__cloudflareProductsSearchPromise = null;
          return null; // always resolve so .then() below always fires
        });
      window.__cloudflareProductsPromise = existingPromise;
      window._cloudflareProductsPromise = existingPromise;
      window.__cloudflareProductsSearchPromise = existingPromise;
    }

    // Attach to whatever promise is running (ours or another script's).
    // .catch() at the end handles the case where the other script's promise rejects.
    existingPromise
      .then(function (data) {
        clearTimeout(fallbackTimer);
        if (Array.isArray(data) && data.length) {
          patchAllCards();
        } else {
          // CF returned empty/null — show placeholders so cards aren't blank
          _showFallbackImages();
        }
      })
      .catch(function (err) {
        clearTimeout(fallbackTimer);
        console.warn('[CollectionGrid] CF promise rejected:', err && err.message);
        _showFallbackImages();
      });
  }


  /* =========================================================================
   * 7. PUBLIC API
   * ======================================================================= */

  window.CollectionGrid = {
    patchAllCards:  patchAllCards,
    patchCard:      patchCard,
    resolveImages:  resolveAllSlideImages,
  };

  window._patchAllProductCards        = patchAllCards;
  window._resolveProductSlideImages   = resolveAllSlideImages;


  /* =========================================================================
   * 8. EVENT WIRING
   * ======================================================================= */

  // Swatch click → update just that card (fast path)
  document.addEventListener('change', function (e) {
    if (!e.target.matches('.swatch-input__input')) return;
    var card = e.target.closest('.card_product_block');
    if (card) patchCardWishlist(card);
  });

  // CF data ready dispatched by custom-product-listing.js or main-collection-product-grid.liquid
  document.addEventListener('cloudflare-products-loaded', patchAllCards);

  // DOM ready: init Swipers immediately, then fetch CF data for images
  function _onReady() {
    _initCards();    // Swiper + wishlist (no network needed)
    _ensureCfData(); // fetch CF data → swap images once it arrives
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', _onReady);
  } else {
    _onReady();
  }

  // Back-navigation (bfcache): reset each card to first swatch + first slide
  window.addEventListener('pageshow', function () {
    document.querySelectorAll('.card_product_block').forEach(function (card) {
      var radios = card.querySelectorAll('.swatch-input__input');
      if (!radios.length) return;
      radios.forEach(function (r, i) { r.checked = (i === 0); });
      var sliderEl = card.querySelector('.card_product_block_img_slider');
      if (sliderEl && sliderEl.swiper) sliderEl.swiper.slideTo(0, 0);
      patchCardWishlist(card);
    });
  });

})();
